import { Event as GQLEvent, EventStateEnum } from '../generated/graphql';
import { take } from '../util/takePayload';
import * as EventSales from '../events/EventSales';
import { Venue } from '../api';
import * as AnnounceDetail from '../api/Announce/AnnounceDetail';
import * as EventGroup from '../api/EventGroup/EventGroup';
import * as SeatingChart from '../api/SeatingChart/SeatingChart';

export type EventType = {
  id: number;
  fullSlug: string;
  name: string;
  holdPreference: number;
  date: string;
  startTime: string;
  doorTime: string;
  endTime: string;
  timezone: string;
  state: EventState;
  ticketed: boolean;
  availableForSalesTax: boolean;
  availableForProcessingFee: boolean;
  mailPdfTickets: boolean;
  availableForDonation: boolean;
  sendWillCallReminders?: boolean;
  minimumAge?: number;
  footerContent?: string;
  confirmationEmailContent?: string;
  ticketLimit?: number;
  ticketSales: EventSales.EventSales;
  venueRoom?: Venue.VenueRoom.VenueRoom;
  venue?: Venue.Venue.Venue;
  announceDetail?: AnnounceDetail.AnnounceDetail;
  eventGroup?: EventGroup.EventGroup;
  seatingChart?: SeatingChart.SeatingChart;
};

export const EventState = Object.freeze({
  HOLD: EventStateEnum.Hold,
  CONFIRMED: EventStateEnum.Confirmed,
  CANCELED: EventStateEnum.Canceled,
  DELETED: EventStateEnum.Deleted,
});

export type EventState = (typeof EventState)[keyof typeof EventState];

export type EventFilter = {
  name?: string;
  startDate?: string | null;
  endDate?: string | null;
  state?: EventState[];
  ticketed?: boolean;
  venueId?: number;
};

export const fromGql = (gqlEvent: GQLEvent): EventType => ({
  ...(take(
    gqlEvent,
    'id',
    'name',
    'holdPreference',
    'date',
    'startTime',
    'doorTime',
    'endTime',
    'state',
    'ticketed',
    'timezone',
    'fullSlug',
    'availableForProcessingFee',
    'availableForSalesTax',
    'availableForDonation',
    'sendWillCallReminders',
    'confirmationEmailContent',
    'footerContent',
    'mailPdfTickets',
    'ticketLimit',
    'minimumAge',
  ) as EventType),
  ticketSales: gqlEvent.ticketSales ? EventSales.fromGql(gqlEvent.ticketSales) : EventSales.getNew(),
  venueRoom: gqlEvent.venueRoom ? Venue.VenueRoom.fromGql(gqlEvent.venueRoom) : undefined,
  venue: gqlEvent.venue ? Venue.Venue.fromGql(gqlEvent.venue) : undefined,
  announceDetail: gqlEvent.announceDetail ? AnnounceDetail.fromGql(gqlEvent.announceDetail) : undefined,
  eventGroup: gqlEvent.eventGroup ? EventGroup.fromGql(gqlEvent.eventGroup) : undefined,
  seatingChart: gqlEvent.seatingChart ? SeatingChart.fromGql(gqlEvent.seatingChart) : undefined,
});

export const fromGqlCollection = (gqlEventCollection: GQLEvent[]): EventType[] =>
  gqlEventCollection.map(fromGql);
